import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2, Ban } from "lucide-react";
import { EnrichedVisitor } from "@/types/database";
import { toPascalCase } from "@/utils/text";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ContactInformation from "./ContactInformation";
import SkillsAndInterests from "./SkillsAndInterests";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from '@tanstack/react-query';
import { getSubscriptionLimits } from "@/utils/subscriptionUtils";
import { processSubscriptionData } from "@/utils/subscriptionDataUtils";

interface PersonalInformationProps {
  visitor: EnrichedVisitor;
  showPersonalInfo: boolean;
  togglePersonalInfo: () => void;
}

const PersonalInformation = ({ visitor, showPersonalInfo, togglePersonalInfo }: PersonalInformationProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [visitorData, setVisitorData] = useState<EnrichedVisitor>(visitor);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const hasPersonalInfo = !!(visitorData.person_name || 
                          visitorData.person_data?.full_name || 
                          visitorData.person_job_title || 
                          visitorData.person_data?.job_title);

  const checkPersonResolutionUsage = async (): Promise<boolean> => {
    try {
      if (!user?.id) {
        console.error('Unable to determine user ID for quota check');
        return true; // Allow by default if we can't determine the user
      }

      // Fetch the complete subscription data to get access to metadata
      const { data: subscriptionData, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription data:', error);
        return true; // Allow by default on error
      }

      if (!subscriptionData) {
        // No subscription found
        console.log('No subscription found for user', user.id);
        return true; // Allow by default - user might be in trial
      }

      // Process the raw subscription data to ensure proper typing
      const subscription = processSubscriptionData(subscriptionData);

      console.log('Subscription data:', subscription);
      console.log('Plan type:', subscription.plan);
      console.log('Metadata:', subscription.metadata);

      // Use the getSubscriptionLimits utility to get proper limits based on the subscription
      const limits = await getSubscriptionLimits(subscription);

      console.log('Resolved limits:', limits);
      console.log('Current usage:', subscription.person_resolutions_used, 'Limit:', limits.personResolutionsLimit);
      
      // Check if the user has remaining quota
      const hasQuotaRemaining = (subscription.person_resolutions_used || 0) < limits.personResolutionsLimit;
      
      // Update the UI state based on quota
      if (!hasQuotaRemaining) {
        setHasReachedLimit(true);
      } else {
        setHasReachedLimit(false);
      }
      
      return hasQuotaRemaining;
    } catch (error) {
      console.error('Error checking person resolution usage:', error);
      return true; // Allow by default
    }
  };

  // Run the check on component mount
  React.useEffect(() => {
    checkPersonResolutionUsage();
  }, []);

  const incrementPersonResolutionUsage = async (): Promise<boolean> => {
    try {
      if (!user?.id) {
        console.error('Unable to determine user ID for updating resolutions usage');
        return false;
      }

      console.log('Incrementing person_resolutions_used for user:', user.id);
      
      // First get the current count
      const { data: currentData, error: fetchError } = await supabase
        .from('subscriptions')
        .select('person_resolutions_used')
        .eq('user_id', user.id)
        .single();
      
      if (fetchError) {
        console.error('Error fetching current person_resolutions_used:', fetchError);
        return false;
      }
      
      const currentCount = currentData?.person_resolutions_used || 0;
      console.log('Current person_resolutions_used count:', currentCount);
      
      // Update with incremented count
      const { data, error } = await supabase
        .from('subscriptions')
        .update({ person_resolutions_used: currentCount + 1 })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating person_resolutions_used:', error);
        return false;
      }
      
      // Invalidate subscription query cache to trigger UI update
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      
      console.log('Successfully incremented person_resolutions_used to', currentCount + 1);
      return true;
    } catch (error) {
      console.error('Exception in incrementPersonResolutionUsage:', error);
      return false;
    }
  };

  const handleExtractClick = async () => {
    if (showPersonalInfo || hasPersonalInfo) {
      togglePersonalInfo();
      return;
    }

    // If the user has reached their limit, show a toast message and return early
    if (hasReachedLimit) {
      toast.error('You have reached your person resolutions limit. Please upgrade your plan to continue.');
      return;
    }

    // Double-check limits right before extraction to handle race conditions
    const allowedToUse = await checkPersonResolutionUsage();
    if (!allowedToUse) {
      setHasReachedLimit(true);
      toast.error('You have reached your person resolutions limit. Please upgrade your plan to continue.');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('search-person', {
        body: { visitor_id: visitor.visitor_id }
      });

      if (error) {
        console.error('Error searching for person:', error);
        toast.error('Failed to extract personal information');
        throw error;
      }

      if (!data.success) {
        console.error('Person search failed:', data.error);
        toast.error(data.error || 'Failed to extract personal information');
        throw new Error(data.error || 'Person search failed');
      }

      if (data.data) {
        setVisitorData({
          ...visitorData,
          ...data.data
        });

        // Increment the person_resolutions_used count after successful extraction
        const updateResult = await incrementPersonResolutionUsage();
        if (!updateResult) {
          console.warn('Failed to increment person resolution count, but extraction succeeded');
        }

        // Recheck limits after incrementing
        checkPersonResolutionUsage();

        toast.success('Personal information extracted successfully');
      } else {
        toast.info('No additional personal information found');
      }

      if (!showPersonalInfo) {
        togglePersonalInfo();
      }
    } catch (error) {
      console.error('Error in person search:', error);
      toast.error('Failed to extract personal information');
    } finally {
      setIsLoading(false);
    }
  };

  const displayedVisitor = visitorData;

  return (
    <div className="space-y-3 w-full">
      <div className="flex justify-between items-center w-full">
        <p className="text-sm font-medium text-gray-500">Personal Information</p>
        <div className="flex items-center">
          {displayedVisitor.person_confidence && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full mr-2">
              Confidence: {toPascalCase(displayedVisitor.person_confidence)}
            </span>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={handleExtractClick} 
                  variant="outline" 
                  size="sm"
                  className={`flex items-center gap-1 ${hasReachedLimit && !hasPersonalInfo && !showPersonalInfo ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                  disabled={isLoading || (hasReachedLimit && !hasPersonalInfo && !showPersonalInfo)}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : showPersonalInfo ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Hide
                    </>
                  ) : hasPersonalInfo ? (
                    <>
                      <Eye className="h-4 w-4" />
                      Show
                    </>
                  ) : hasReachedLimit ? (
                    <>
                      <Ban className="h-4 w-4" />
                      Limit Reached
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Extract
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {showPersonalInfo ? "Hide personal information" : 
                 hasPersonalInfo ? "Show personal information" : 
                 hasReachedLimit ? "You've reached your person resolution limit. Please upgrade your plan." :
                 "Extract personal information"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
      {showPersonalInfo ? (
        <div className="animate-in fade-in-50 duration-300 w-full">
          {(displayedVisitor.person_name || displayedVisitor.person_data?.full_name) && (
            <div className="w-full mb-2">
              <p className="text-xs text-gray-500">Name</p>
              <p className="text-sm font-medium break-words">{displayedVisitor.person_name || displayedVisitor.person_data?.full_name}</p>
            </div>
          )}
          
          {(displayedVisitor.person_job_title || displayedVisitor.person_data?.job_title) && (
            <div className="w-full mb-2">
              <p className="text-xs text-gray-500">Job Title</p>
              <p className="text-sm break-words">{displayedVisitor.person_job_title || displayedVisitor.person_data?.job_title}</p>
            </div>
          )}
          
          {displayedVisitor.person_sex && (
            <div className="w-full mb-2">
              <p className="text-xs text-gray-500">Gender</p>
              <p className="text-sm">{toPascalCase(displayedVisitor.person_sex)}</p>
            </div>
          )}
          
          {displayedVisitor.person_data?.industry && (
            <div className="w-full mb-2">
              <p className="text-xs text-gray-500">Industry</p>
              <p className="text-sm break-words">{toPascalCase(displayedVisitor.person_data.industry)}</p>
            </div>
          )}
          
          {displayedVisitor.location_locality && displayedVisitor.location_country && (
            <div className="w-full mb-2">
              <p className="text-xs text-gray-500">Personal Location</p>
              <p className="text-sm break-words">{toPascalCase(displayedVisitor.location_locality)}, {toPascalCase(displayedVisitor.location_country)}</p>
            </div>
          )}
          
          <ContactInformation visitor={displayedVisitor} />
          
          <SkillsAndInterests visitor={displayedVisitor} />
        </div>
      ) : (
        <div className="text-center p-4 border border-dashed border-gray-300 rounded-md bg-gray-50 w-full">
          {hasReachedLimit && !hasPersonalInfo ? (
            <>
              <p className="text-sm text-gray-500">Person resolution limit reached</p>
              <p className="text-xs text-gray-400 mt-1">Please upgrade your plan to extract more personal information</p>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500">Personal information is hidden</p>
              <p className="text-xs text-gray-400 mt-1">Click the "Extract" button to view contact details and personal information</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalInformation;
